// src/utils/codegen.js
// Utilities to generate Velocity and equivalent Python code for a given JSONPath
// Example input path: $document.AsnLine[0].BatchNumber

/** Public API: build ONLY Velocity */
export function buildVelocityCode(pathString) {
  if (!pathString) return "";
  const velocity = formatVelocityIndent(generateVelocityCode(pathString));

  // If the path contains an array, also provide a direct object access variant
  if (/\[/.test(pathString)) {
    return `${velocity}\n(OR)\n\n${generateVelocityCodeForObject(pathString)}`.trimEnd();
  }
  return velocity.trimEnd();
}

/** Public API: build ONLY Python */
export function buildPythonCode(pathString) {
  return generatePythonCode(pathString).trimEnd();
}

/** Public API: build Velocity + (OR) + Python */
export function buildCombinedCode(pathString) {
  const velocity = buildVelocityCode(pathString);
  const python = buildPythonCode(pathString);
  return `${velocity}\n\n(OR)\n\n${python}`.trimEnd();
}

/* -------------------- Velocity generation -------------------- */

function generateVelocityCodeForObject(pathString) {
  // e.g. $document.AsnLine[0].BatchNumber  ->  #set($BatchNumber = $document.AsnLine[0].BatchNumber)
  const tail = pathString.substring(pathString.lastIndexOf(".") + 1).replace(/\[\d+\]$/, "");
  return `#set($${tail} = ${pathString})\n`;
}

function generateVelocityCodeForArray(pathString) {
  // Build nested foreach blocks when arrays appear in the tail.
  // Example: $document.company.employees[0].skills[0].name
  // -> #foreach($employees in $document.company.employees)
  //      #foreach($skills in $employees.skills)
  //        #set($name = $skills.name)
  //    #end
  //    #end
  const idx = pathString.indexOf("[");
  const arrayPath = pathString.substring(0, idx); // e.g. $document.company.employees
  const foreachVar = `$${arrayPath.substring(arrayPath.lastIndexOf(".") + 1)}`; // $employees
  let code = `#foreach(${foreachVar} in ${arrayPath}) \n`;

  // Replace the array segment with foreach loop var, recurse for remaining
  const remaining = pathString
    .replace(arrayPath, "")
    .replace(/\[\d+\]/, foreachVar); // e.g. .$skills[0].name  (after first loop)

  if (remaining !== foreachVar) {
    code += generateVelocityCode(remaining);
  }
  code += `#end \n`;
  return code;
}

function generateVelocityCode(pathString) {
  return /\[/.test(pathString)
    ? generateVelocityCodeForArray(pathString)
    : generateVelocityCodeForObject(pathString);
}

function formatVelocityIndent(input) {
  const lines = input.split("\n");
  let out = "";
  let tab = "";
  let firstSet = true;
  let firstForeach = true;

  for (const line of lines) {
    if (!line.trim()) continue;

    if (line.includes("#foreach")) {
      if (firstForeach) {
        firstForeach = false;
        out += line + "\n";
      } else {
        tab += "    ";
        out += tab + line + "\n";
      }
      continue;
    }

    if (line.includes("#set")) {
      if (firstSet) {
        firstSet = false;
        tab += "    ";
      }
      out += tab + line + "\n";
      continue;
    }

    if (line.includes("#end")) {
      tab = tab.substring(0, Math.max(0, tab.length - 4));
      out += tab + line + "\n";
      continue;
    }

    out += tab + line + "\n";
  }
  return out;
}


/* -------------------- Python generation (nested loops) -------------------- */

function generatePythonCode(pathString) {
  if (!pathString) return "# Python equivalent\n";

  // Remove leading $document. and split into parts
  const clean = pathString.replace(/^\$document\.?/, "");
  const parts = clean.split(".").filter(Boolean); // e.g. ["AsnLine[0]", "AsnId"]

  // Helper to parse a segment like "AsnLine[0]" -> { key: "AsnLine", hasIndex: true }
  const parsePart = (part) => {
    const m = part.match(/^([A-Za-z_]\w*)(\[(\d+)\])?$/);
    if (!m) return { key: part, hasIndex: false, index: null };
    return { key: m[1], hasIndex: !!m[2], index: m[3] || null };
  };

  // Make unique loop variable names if same array name repeats
  const varCount = new Map();
  const loopVarFor = (baseKey) => {
    const base = sanitizeVar(baseKey) || "item";
    const n = (varCount.get(base) || 0) + 1;
    varCount.set(base, n);
    return n === 1 ? base : `${base}${n}`;
  };

  const lines = ["# Python equivalent"];
  let indent = 0;
  let expr = "document"; // current reference (document or loop var)

  for (let i = 0; i < parts.length; i++) {
    const { key, hasIndex } = parsePart(parts[i]);

    if (hasIndex) {
      // When encountering an array segment, create a for-loop over document["key"]
      const loopVar = loopVarFor(key);
      lines.push(`${"    ".repeat(indent)}for ${loopVar} in ${expr}["${key}"]:`);

      indent += 1;
      expr = loopVar; // inside loop refer to loop variable
    } else {
      // Non-array segment: navigate into expression using bracket notation
      expr = `${expr}["${key}"]`;
    }
  }

  // Final assignment — last property name (strip any [0] if present)
  const lastBaseRaw = parts.length ? parts[parts.length - 1].replace(/\[\d+\]/g, "") : "value";
  const assignName = sanitizeVar(lastBaseRaw) || "value";
  lines.push(`${"    ".repeat(indent)}${assignName} = ${expr}`);

  return lines.join("\n");
}



function sanitizeVar(name) {
  const cleaned = (name || "").replace(/\W/g, "_");
  // Prevent names starting with digits in Python
  return cleaned.replace(/^(\d)/, "_$1");
}
